import { AfterViewInit, Component, Inject } from '@angular/core';
import { skip, tap } from 'rxjs/operators';
import * as angular from 'angular';
import { downgradeComponent } from '@angular/upgrade/static';
import { Store } from '@ngrx/store';
import { GenericListType, LoadMoreQuery } from '../../common/components/lists/generic-list.component';
import { AppState } from '../../store';
import {
  closeConnectionsModal,
  getConnectionItem,
  getConnectionsList,
  loadMoreConnectionsList,
  openConnectionsModal,
} from '../store/connections.actions';
import {
  selectAreAllConnectionsLoadedFlag,
  selectConnections,
  selectConnectionsIsModalOpenFlag,
  selectConnectionsLoadingFlag,
} from '../store/connections.selectors';
import { SearchConfig } from '../../common/services/assisted-search/search-config.model';
import { AnyConnection } from '../connection.models';
import { QueryParamsConnectionsList } from '../../common/helper/query-params-generic-list.helper';

@Component({
  selector: 'connections-list',
  template: `
    <generic-list
      [type]="type"
      [items]="connections$ | async"
      [isLoading]="connectionsLoading$ | async"
      [hasNewButton]="true"
      (createClick)="openConnectionCreateModal()"
      [newBtnText]="'connection.generic-object.buttons.new' | translate"
      [emptyTitle]="'connection.generic-object.empty.title' | translate"
      [emptyDescription]="'connection.generic-object.empty.description' | translate"
      (loadMore)="loadMoreConnections($event)"
      [limit]="queryParams.limit"
      [allItemsLoaded]="areAllConnectionsLoaded$ | async"
      [searchPlaceholder]="'connection.generic-object.placeholders.search' | translate"
      [searchConfig]="searchConfig"
      [searchQueryFromUrl]="searchQueryFromUrl"
      [hasSearch]="false"
    >
      <ng-template templateName="listItem" let-item>
        <connection-list-item [item]="item" (edit)="editConnection($event)"></connection-list-item>
      </ng-template>
      <ng-template templateName="headers">
        <div class="generic-list-headers connections">
          <div class="name">
            <span (click)="changeOrder('name')">
              <span class="sorting">{{ 'generic-list.connections.name' | translate }}</span>
              <i
                class="fa"
                [ngClass]="{
                  active: queryParams.sort === 'name',
                  'fa-chevron-down': queryParams.direction === 'asc' && queryParams.sort === 'name',
                  'fa-chevron-up': queryParams.direction === 'desc' && queryParams.sort === 'name'
                }"
              ></i>
            </span>
          </div>

          <div class="type">
            <span (click)="changeOrder('type')">
              <span class="sorting">{{ 'generic-list.connections.type' | translate }}</span>
              <i
                class="fa"
                [ngClass]="{
                  active: queryParams.sort === 'type',
                  'fa-chevron-down': queryParams.direction === 'asc' && queryParams.sort === 'type',
                  'fa-chevron-up': queryParams.direction === 'desc' && queryParams.sort === 'type'
                }"
              ></i>
            </span>
          </div>

          <div class="owner">{{ 'generic-list.connections.owner' | translate }}</div>
          <div class="date">
            <span (click)="changeOrder('updated')">
              <span class="sorting">{{ 'generic-list.connections.date' | translate }}</span>
              <i
                class="fa"
                [ngClass]="{
                  active: queryParams.sort === 'updated',
                  'fa-chevron-down': queryParams.direction === 'desc' && queryParams.sort === 'updated',
                  'fa-chevron-up': queryParams.direction === 'asc' && queryParams.sort === 'updated'
                }"
              ></i>
            </span>
          </div>
        </div>
      </ng-template>
    </generic-list>
    <xp-modal
      [isOpen]="isConnectionModalOpen$ | async"
      [closeButtonText]="'connection.generic-object.buttons.close' | translate"
      [saveButtonText]="'connection.generic-object.buttons.save' | translate"
      [titleText]="'connection.generic-object.title' | translate"
      (close)="onConnectionModalClose()"
    >
      <ng-template>
        <connection-form [item]="formItem"></connection-form>
      </ng-template>
    </xp-modal>
  `,
})
export class ConnectionsListComponent implements AfterViewInit {
  type = GenericListType.connections;
  connectionsLoading$ = this.store.select(selectConnectionsLoadingFlag);
  isConnectionModalOpen$ = this.store.select(selectConnectionsIsModalOpenFlag).pipe(
    skip(1),
    tap((isOpen) => {
      if (!isOpen) {
        this.$rootScope.stateGo('app.account.connections');
      }
    }),
  );
  connections$ = this.store.select(selectConnections);
  areAllConnectionsLoaded$ = this.store.select(selectAreAllConnectionsLoadedFlag);
  queryParams = QueryParamsConnectionsList;
  formItem: Partial<AnyConnection> = {};
  searchQueryFromUrl = '';
  searchConfig: SearchConfig = {};

  constructor(
    @Inject('$rootScope') private $rootScope: any,
    @Inject('$stateParams') private $stateParams: any,
    private store: Store<AppState>,
  ) {
    this.store.dispatch(getConnectionsList({ params: this.queryParams }));
  }

  ngAfterViewInit() {
    const isTypeNewState = this.$rootScope.stateIs('app.account.connections.type.new');
    if (this.$rootScope.stateIs('app.account.connections.new') || isTypeNewState) {
      this.openConnectionCreateModal(isTypeNewState);
    }

    if (this.$rootScope.stateIs('app.account.connections.type.item.edit')) {
      this.editConnection({ type: this.$stateParams.type, id: this.$stateParams.id });
    }
  }

  changeOrder(field: string) {
    let { direction } = this.queryParams;
    if (field === this.queryParams.sort) {
      direction = direction === 'asc' ? 'desc' : 'asc';
    }
    this.queryParams = { ...this.queryParams, sort: field, direction };
    this.store.dispatch(getConnectionsList({ params: this.queryParams }));
  }

  editConnection(item: Partial<AnyConnection>) {
    this.store.dispatch(getConnectionItem({ connectionId: item.id, connectionType: item.type }));
    this.formItem = { ...item };
    this.$rootScope.stateGo('app.account.connections.type.item.edit', {
      id: item.id,
      type: item.type,
    });
    this.store.dispatch(openConnectionsModal());
  }

  openConnectionCreateModal(isTypeSelected = false): void {
    this.formItem = {};
    this.$rootScope.stateGo(isTypeSelected ? 'app.account.connections.type.new' : 'app.account.connections.new');
    this.store.dispatch(openConnectionsModal());
  }

  onConnectionModalClose(): void {
    this.$rootScope.stateGo('app.account.connections');
    this.store.dispatch(closeConnectionsModal());
  }

  loadMoreConnections(query: LoadMoreQuery) {
    const params: any = {
      ...this.queryParams,
      ...query,
    };
    this.store.dispatch(loadMoreConnectionsList({ params }));
  }
}

((window as any).xplenty as angular.IModule).directive(
  'connectionsList',
  downgradeComponent({
    component: ConnectionsListComponent,
  }),
);
